import ProgramRewardsModal from '@/components/actions/program/ProgramRewardsModal';
import Money from '@/components/text/money';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuth } from '@/contexts/auth-context';
import {
  formatFirstOrderCommissionSummary,
  getFirstOrderCommission,
} from '@/components/program/first-order-commission';
import { Badge, Box, Button, Card, Flex, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import {
  CommissionTypes,
  BrandAccountType,
  hasCommissionCategoryGrid,
  hasSingleCommissionRate,
  type Program,
} from '@fluenceur/types';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditPencil, GraphUp } from 'iconoir-react';

interface ProgramOverviewRewardsProps {
  program: Program;
}

const ProgramOverviewRewards: React.FC<ProgramOverviewRewardsProps> = ({ program }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { commissionSettings } = program;
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);

  const platformFeeModel = user?.currentBrand?.platformFeeModel;
  const platformFeeRate = user?.currentBrand?.platformFeeRate ?? 0.05;
  const currency = user?.currentBrand?.currency ?? 'EUR';

  // Check if commissions are enabled
  const hasCommissionsEnabled = commissionSettings?.enabled;
  const hasCategoryGrid = hasCommissionCategoryGrid(commissionSettings);
  const hasSingleRate = hasSingleCommissionRate(commissionSettings);
  const firstOrderCommission = getFirstOrderCommission(commissionSettings);
  const firstOrderSummary = formatFirstOrderCommissionSummary(commissionSettings, {
    locale: i18n.language,
    currency,
    t,
  });

  return (
    <>
      <Card.Root w="full" p={8}>
        <Card.Header p={0}>
          <Flex justifyContent="space-between" alignItems="baseline">
            <Card.Title fontSize="2xl">{t('programs.programOverview.rewards.title')}</Card.Title>
            <Button
              variant="outline"
              w="fit-content"
              size="sm"
              onClick={() => setIsModalFormOpen(true)}
            >
              <EditPencil width={20} height={20} />
              {hasCommissionsEnabled
                ? t('programs.programOverview.rewards.edit')
                : t('programs.programOverview.rewards.add')}
            </Button>
          </Flex>
        </Card.Header>
        <Card.Body p={0} mt={8}>
          {hasCategoryGrid ? (
            <VStack align="stretch" gap={3}>
              <Text fontSize="sm" color="gray.500">
                {t('programs.programOverview.rewards.commissionCategories')} · v
                {commissionSettings.categoryGrid.version}
              </Text>
              {commissionSettings.categoryGrid.categories.map((category) => (
                <HStack
                  key={category.key}
                  justify="space-between"
                  borderWidth="1px"
                  rounded="lg"
                  px={4}
                  py={3}
                >
                  <VStack align="start" gap={0}>
                    <Text fontWeight="medium">{category.label}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {category.key}
                    </Text>
                  </VStack>
                  <HStack>
                    <Badge colorPalette="gray" variant="subtle">
                      {category.type === CommissionTypes.Percentage
                        ? t('programs.programOverview.rewards.percentage')
                        : t('programs.programOverview.rewards.fixedAmount')}
                    </Badge>
                    <Text fontWeight="semibold">
                      {category.type === CommissionTypes.Percentage ? (
                        `${category.value}%`
                      ) : (
                        <Money value={category.value} currency={currency} />
                      )}
                    </Text>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          ) : hasSingleRate ? (
            <DataListRoot orientation="vertical" gap={6}>
              {firstOrderSummary ? (
                <Box>
                  <Text fontWeight="medium">{firstOrderSummary}</Text>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {t(
                      firstOrderCommission?.trigger === 'customer_first_order'
                        ? 'programs.modals.editRewards.customerFirstOrderEligibility'
                        : 'programs.modals.editRewards.firstOrderCommissionEligibility'
                    )}
                  </Text>
                  {firstOrderCommission?.trigger !== 'customer_first_order' ? (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      {t('programs.modals.editRewards.firstOrderCommissionOverride')}
                    </Text>
                  ) : null}
                </Box>
              ) : null}
              <SimpleGrid
                columns={[1, null, 3]}
                gap={6}
                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
              >
                <DataListItem
                  label={t('programs.programOverview.rewards.commissionType')}
                  alignItems="baseline"
                  value={
                    <Text fontWeight="medium" fontSize="md">
                      {commissionSettings.type === 'percentage'
                        ? t('programs.programOverview.rewards.percentage')
                        : t('programs.programOverview.rewards.fixedAmount')}
                    </Text>
                  }
                />
                <DataListItem
                  label={t(
                    firstOrderCommission
                      ? 'programs.programOverview.rewards.followingCommissionValue'
                      : 'programs.programOverview.rewards.commissionValue'
                  )}
                  alignItems="baseline"
                  value={
                    <Text fontSize="md" fontWeight="medium">
                      {commissionSettings.type === 'percentage' ? (
                        `${commissionSettings.value}%`
                      ) : (
                        <Money value={commissionSettings.value} currency={currency} />
                      )}
                    </Text>
                  }
                />
              </SimpleGrid>
              {user?.currentBrand?.accountType !== BrandAccountType.SHOPIFY &&
                platformFeeModel === 'affiliate_deducted' &&
                commissionSettings.value > 0 && (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {t(
                      commissionSettings.type === 'percentage'
                        ? 'programs.programOverview.rewards.affiliateNetHintPercentage'
                        : 'programs.programOverview.rewards.affiliateNetHintFixed',
                      {
                        feePercent: (platformFeeRate * 100).toFixed(2).replace(/\.?0+$/, ''),
                        netValue: (commissionSettings.value * (1 - platformFeeRate)).toFixed(2),
                      }
                    )}
                  </Text>
                )}
            </DataListRoot>
          ) : (
            <EmptyState
              title={t('programs.programOverview.rewards.noRewardsTitle')}
              description={t('programs.programOverview.rewards.noRewardsDesc')}
              icon={<GraphUp />}
              py={6}
            />
          )}
        </Card.Body>
      </Card.Root>

      <ProgramRewardsModal
        programId={program.id}
        programMode={program.mode}
        isOpen={isModalFormOpen}
        onClose={() => setIsModalFormOpen(false)}
        defaultValues={{
          commissionType: commissionSettings?.type,
          commissionValue: commissionSettings?.value,
          commissionCategories: commissionSettings?.categoryGrid?.categories,
          applyCommission: commissionSettings?.enabled,
          applyFirstOrderCommission: commissionSettings?.firstOrderCommission?.enabled,
          firstOrderCommissionTrigger: commissionSettings?.firstOrderCommission?.trigger,
          firstOrderCommissionType: commissionSettings?.firstOrderCommission?.type,
          firstOrderCommissionValue: commissionSettings?.firstOrderCommission?.value,
        }}
      />
    </>
  );
};

export default ProgramOverviewRewards;
